import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

type ConsentStatus = 'pending' | 'granted' | 'denied';

interface CookieConsentContextType {
  consentStatus: ConsentStatus;
  hasConsented: boolean;
  acceptCookies: () => void;
  declineCookies: () => void;
  resetConsent: () => void;
}

const CONSENT_STORAGE_KEY = 'analytics_consent';

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

// Helper to update Google Analytics consent
function updateGtagConsent(granted: boolean) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      'analytics_storage': granted ? 'granted' : 'denied',
    });
    
    if (import.meta.env.DEV) {
      console.log('[GA4 Consent] Updated to:', granted ? 'granted' : 'denied');
    }
  }
}

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(() => {
    if (typeof window === 'undefined') return 'pending';
    
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored === 'granted') return 'granted';
    if (stored === 'denied') return 'denied';
    return 'pending';
  });

  // Apply consent on mount if already set
  useEffect(() => {
    if (consentStatus === 'granted') {
      updateGtagConsent(true);
    } else if (consentStatus === 'denied') {
      updateGtagConsent(false);
    }
  }, []);

  const acceptCookies = useCallback(() => {
    setConsentStatus('granted');
    localStorage.setItem(CONSENT_STORAGE_KEY, 'granted');
    updateGtagConsent(true);
  }, []);

  const declineCookies = useCallback(() => {
    setConsentStatus('denied');
    localStorage.setItem(CONSENT_STORAGE_KEY, 'denied');
    updateGtagConsent(false);
  }, []);

  const resetConsent = useCallback(() => {
    setConsentStatus('pending');
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  }, []);

  const hasConsented = consentStatus !== 'pending';

  return (
    <CookieConsentContext.Provider
      value={{
        consentStatus,
        hasConsented,
        acceptCookies,
        declineCookies,
        resetConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}
